import React from "react";
import { useTheme } from "./theme";

export default function Features() {
  const { theme } = useTheme();
  const palette = theme.palette;
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia === "undefined") {
      return false;
    }
    return window.matchMedia("(max-width: 768px)").matches;
  });
  const [showScrollHint, setShowScrollHint] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia === "undefined") {
      return;
    }
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    setIsMobile(mediaQuery.matches);

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  React.useEffect(() => {
    if (isMobile) {
      setShowScrollHint(true);
    } else {
      setShowScrollHint(false);
    }
  }, [isMobile]);

  const handleCarouselScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!showScrollHint) return;
      if (event.currentTarget.scrollLeft > 12) {
        setShowScrollHint(false);
      }
    },
    [showScrollHint]
  );

  const items = [
    {
      title: "Zero Setup",
      desc: "No repo access. Just your site URL. Privacy-first testing in seconds.",
    },
    {
      title: "Instant Feedback",
      desc: "Pass/fail checks with screenshots & logs, right in your dashboard.",
    },
    {
      title: "Human QA Add-On",
      desc: "Send failed runs to real testers for deeper review & expert reports.",
    },
  ];

  return (
    <section
      id="features"
      style={{
        padding: "1rem 1rem",
        textAlign: "center",
        background: palette.backgroundAlt,
        color: palette.textPrimary,
      }}
    >
      <h2
        style={{
          fontSize: "2rem",
          fontWeight: 800,
          marginBottom: "2rem",
          color: palette.textPrimary,
        }}
      >
        Why LaunchSafely?
      </h2>

      <div
        style={{
          display: isMobile ? "flex" : "grid",
          gridTemplateColumns: isMobile
            ? undefined
            : "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "1.5rem",
          maxWidth: "1000px",
          margin: "0 auto",
          overflowX: isMobile ? "auto" : "visible",
          padding: isMobile ? "0 0.5rem 1rem" : undefined,
          scrollSnapType: isMobile ? "x mandatory" : undefined,
          WebkitOverflowScrolling: isMobile ? "touch" : undefined,
          scrollPadding: isMobile ? "0 1rem" : undefined,
        }}
        onScroll={isMobile ? handleCarouselScroll : undefined}
      >
        {items.map((f) => (
          <div
            key={f.title}
            style={{
              background: palette.card,
              border: `1px solid ${palette.border}`,
              borderRadius: "16px",
              padding: "1.5rem",
              textAlign: "left",
              boxShadow: palette.shadow,
              flex: isMobile ? "0 0 85%" : undefined,
              minWidth: isMobile ? "85%" : undefined,
              scrollSnapAlign: isMobile ? "center" : undefined,
              scrollSnapStop: isMobile ? "always" : undefined,
            }}
          >
            <h3
              style={{
                backgroundImage: palette.accentGradient,
                WebkitBackgroundClip: "text",
                color: "transparent",
                marginBottom: "0.5rem",
                fontWeight: 600,
                fontSize: "1.1rem",
              }}
            >
              {f.title}
            </h3>
            <p style={{ color: palette.textMuted, lineHeight: 1.6 }}>
              {f.desc}
            </p>
          </div>
        ))}
      </div>
      {showScrollHint && isMobile && (
        <div
          style={{
            marginTop: "0.25rem",
            fontSize: "0.85rem",
            color: palette.textMuted,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.4rem",
            userSelect: "none",
          }}
        >
          <span aria-hidden="true" style={{ fontSize: "1rem", letterSpacing: "0.2rem" }}>
            {"< >"}
          </span>
          Swipe for more reasons
        </div>
      )}
    </section>
  );
}
